import React, { Component } from 'react';
import { Button, Result } from 'antd';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import ConfigurationController from './configuration.controller';
import { Layout } from '../common';
import Loading from '../common/Loading';
import SegmentationStep from './elements/SegmentationStep';
import AvailableBalanceStep from './elements/AvailableBalanceStep';
import FeeStructureStep from './elements/FeeStructureStep';
import LimitsStep from './elements/LimitsStep';
import ConfigurationSuccess from './elements/ConfigurationSuccess';

class Configuration extends Inherit(ConfigurationController, Component) {
    state = {
        // Step management
        currentStep: 0,
        firstTimeSetup: true,

        // Segmentation state (consolidated in segment_policy)
        segment_policy: {
            mode: 'restricted', // 'disabled', 'whitelist', 'restricted', 'open'
            whitelist: [], // Array of member IDs for whitelist mode
            bankingTenure: '',
            accountTypes: '',
            avgDailyBalance: '',
            overdraftHistory: '',
            returnedACHs: '',
            blockOutOfSegmentation: false // Block users who fall out of segmentation criteria
        },
        // Enabled state for each segmentation field
        segmentation_enabled: {
            bankingTenure: true,
            accountTypes: true,
            avgDailyBalance: true,
            overdraftHistory: true,
            returnedACHs: true
        },
        // UI state for whitelist management
        whitelistInput: '',

        // Available Balance state (stored as decimal, e.g., 0.75 = 75%)
        threshold: 0.75,
        doubleDipCap: 0.5,
        balance_rules: {
            userTenureRules: [{ id: 1, condition: '60-89-days', deduction: 10 }],
            overdraftRules: [],
            achRules: [],
            employmentRules: [],
            balanceRules: []
        },
        balance_enabled: {
            userTenure: true,
            overdraft: true,
            ach: false,
            employment: false,
            balance: false
        },

        // Fee Structure state
        fee: 3.0,
        fee_policy: {
            standardFreeAdvanceTime: 1,
            standardFreeAdvanceUnit: 'business-days',
            feeInstance: 'on-advance',
            feeStrategy: 'on-top-of-advance'
        },
        fee_rules: {
            volumeTierRules: [{ id: 1, condition: 'less-than-50', fee: 1.0 }],
            freeAdvanceRules: [{ id: 1, count: 1, condition: 'first-advance' }]
        },
        fee_enabled: {
            instantFee: true,
            volumeTiers: true,
            freeAdvances: true
        },

        // Limits state (consolidated in limit_policy)
        limit_policy: {
            maxAdvances: 12,
            advanceTimeframe: 'pay-period',
            minAdvanceAmount: 5,
            maxAdvanceAmount: 700,
            maxAdvanceVolume: 1500
        },
        limits_enabled: {
            maxAdvances: true,
            advanceAmounts: true,
            totalVolume: true
        },

        // Common state
        loading: true, // Start with loading true until we determine firstTimeSetup
        error: false,
        user: null
    };

    render() {
        const { currentStep, firstTimeSetup, loading, error, user } = this.state;

        // Show loader while determining firstTimeSetup status
        if (loading) {
            return (
                <Layout user={user}>
                    <Loading />
                </Layout>
            );
        }

        // Show error state if loading failed
        if (error) {
            return (
                <Layout user={user}>
                    <Result
                        status="error"
                        title="Error Loading Configuration"
                        subTitle="We couldn't load your configuration. Please try again."
                        extra={
                            <Button type="primary" onClick={() => window.location.reload()}>
                                Retry
                            </Button>
                        }
                    />
                </Layout>
            );
        }

        // Render success screen separately (no layout)
        if (currentStep === 4) {
            return <ConfigurationSuccess onContinue={this.handleContinue} />;
        }

        // Render appropriate step
        let stepComponent;
        switch (currentStep) {
            case 0:
                stepComponent = (
                    <SegmentationStep
                        state={this.state}
                        firstTimeSetup={firstTimeSetup}
                        setBankingTenure={this.setBankingTenure.bind(this)}
                        setAccountTypes={this.setAccountTypes.bind(this)}
                        setAvgDailyBalance={this.setAvgDailyBalance.bind(this)}
                        setOverdraftHistory={this.setOverdraftHistory.bind(this)}
                        setReturnedACHs={this.setReturnedACHs.bind(this)}
                        toggleFieldEnabled={this.toggleFieldEnabled.bind(this)}
                        setSegmentationMode={this.setSegmentationMode.bind(this)}
                        setWhitelistInput={this.setWhitelistInput.bind(this)}
                        addToWhitelist={this.addToWhitelist.bind(this)}
                        removeFromWhitelist={this.removeFromWhitelist.bind(this)}
                        setBlockOutOfSegmentation={this.setBlockOutOfSegmentation.bind(this)}
                        getEligibilitySummary={this.getEligibilitySummary.bind(this)}
                        hasChanges={this.hasSegmentChanges.bind(this)}
                        onSubmit={this.handleSubmitSegmentation.bind(this)}
                        onContinue={this.handleContinue}
                        onCancel={firstTimeSetup ? this.handleCancel : this.handleCancelSegmentation.bind(this)}
                        onStepChange={this.handleStepChange}
                    />
                );
                break;
            case 1:
                stepComponent = (
                    <AvailableBalanceStep
                        state={this.state}
                        firstTimeSetup={firstTimeSetup}
                        setThreshold={this.setThreshold.bind(this)}
                        setDoubleDipCap={this.setDoubleDipCap.bind(this)}
                        addRule={this.addRule.bind(this)}
                        removeRule={this.removeRule.bind(this)}
                        updateRule={this.updateRule.bind(this)}
                        toggleRuleGroup={this.toggleBalanceRuleGroup.bind(this)}
                        getAvailableBalanceSummary={this.getAvailableBalanceSummary.bind(this)}
                        hasChanges={this.hasBalanceChanges.bind(this)}
                        onSubmit={this.handleSubmitBalance.bind(this)}
                        onContinue={this.handleContinue}
                        onCancel={firstTimeSetup ? this.handleCancel : this.handleCancelBalance.bind(this)}
                        onStepChange={this.handleStepChange}
                    />
                );
                break;
            case 2:
                stepComponent = (
                    <FeeStructureStep
                        state={this.state}
                        firstTimeSetup={firstTimeSetup}
                        setFee={this.setFee.bind(this)}
                        setStandardFreeAdvanceTime={this.setStandardFreeAdvanceTime.bind(this)}
                        setFeeInstance={this.setFeeInstance.bind(this)}
                        setFeeStrategy={this.setFeeStrategy.bind(this)}
                        addVolumeTierRule={this.addVolumeTierRule.bind(this)}
                        removeVolumeTierRule={this.removeVolumeTierRule.bind(this)}
                        updateVolumeTierRule={this.updateVolumeTierRule.bind(this)}
                        addFreeAdvanceRule={this.addFreeAdvanceRule.bind(this)}
                        removeFreeAdvanceRule={this.removeFreeAdvanceRule.bind(this)}
                        updateFreeAdvanceRule={this.updateFreeAdvanceRule.bind(this)}
                        toggleFeeSection={this.toggleFeeSection.bind(this)}
                        hasChanges={this.hasFeeChanges.bind(this)}
                        onSubmit={this.handleSubmitFee.bind(this)}
                        onContinue={this.handleContinue}
                        onCancel={firstTimeSetup ? this.handleCancel : this.handleCancelFee.bind(this)}
                        onStepChange={this.handleStepChange}
                    />
                );
                break;
            case 3:
                stepComponent = (
                    <LimitsStep
                        state={this.state}
                        firstTimeSetup={firstTimeSetup}
                        setMaxAdvances={this.setMaxAdvances.bind(this)}
                        setAdvanceTimeframe={this.setAdvanceTimeframe.bind(this)}
                        setMinAdvanceAmount={this.setMinAdvanceAmount.bind(this)}
                        setMaxAdvanceAmount={this.setMaxAdvanceAmount.bind(this)}
                        setMaxAdvanceVolume={this.setMaxAdvanceVolume.bind(this)}
                        toggleLimitsSection={this.toggleLimitsSection.bind(this)}
                        getLimitsSummary={this.getLimitsSummary.bind(this)}
                        hasChanges={this.hasLimitChanges.bind(this)}
                        onSubmit={this.handleSubmitLimits.bind(this)}
                        onContinue={this.handleContinue}
                        onCancel={firstTimeSetup ? this.handleCancel : this.handleCancelLimits.bind(this)}
                        onStepChange={this.handleStepChange}
                    />
                );
                break;
            default:
                stepComponent = null;
        }

        return stepComponent;
    }
}

export default withHooks(Configuration);
